import React, { useState, useEffect, useCallback } from "react";
import {
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  GiftIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  UserIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { authService } from "../services/authService";
import { apiService, AppSettings } from "../services/api";
import toast from "react-hot-toast";
import CustomToast from "../components/common/CustomToast";
import { familyAdminErrorMessage } from "../utils/familyAdminErrors";
import {
  FAMILY_INTEGER_SETTING_RULES,
  FAMILY_SETTING_DEFAULTS,
  FamilyIntegerSettingKey,
  familyAllowlistError,
  familyIntegerSettingWarning,
  formatFamilyAllowlist,
  nonCustomerIdEntries,
  parseFamilyAllowlist,
  parseFamilyIntegerSetting,
  readFamilyAllowlist,
} from "../utils/familySettings";

const showToast = (
  message: string,
  type: "success" | "error" | "warning",
) => {
  toast.custom(<CustomToast message={message} type={type} />, {
    duration: type === "error" ? 5000 : 4000,
  });
};

const switchClass = (on: boolean) =>
  `relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
    on ? "bg-primary-600" : "bg-gray-300 dark:bg-gray-600"
  }`;

const switchKnobClass = (on: boolean) =>
  `inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
    on ? "translate-x-6" : "translate-x-1"
  }`;

type FamilyLoadState = "loading" | "ready" | "error";

type FamilyConfirm = "memberLoginOff" | "clearAllowlist" | null;

const INTEGER_KEYS: FamilyIntegerSettingKey[] = [
  "familyMaxMembers",
  "familyInviteTtlDays",
  "familyInviteDailyLimit",
];

const integerValuesFrom = (
  settings: Partial<AppSettings> | null,
): Record<FamilyIntegerSettingKey, string> => ({
  familyMaxMembers: String(
    settings?.familyMaxMembers ?? FAMILY_SETTING_DEFAULTS.familyMaxMembers,
  ),
  familyInviteTtlDays: String(
    settings?.familyInviteTtlDays ?? FAMILY_SETTING_DEFAULTS.familyInviteTtlDays,
  ),
  familyInviteDailyLimit: String(
    settings?.familyInviteDailyLimit ??
      FAMILY_SETTING_DEFAULTS.familyInviteDailyLimit,
  ),
});

interface FamilyConfirmDialogProps {
  title: string;
  body: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}

// Same look as the Payouts reject modal: red header, grey footer.
const FamilyConfirmDialog: React.FC<FamilyConfirmDialogProps> = ({
  title,
  body,
  confirmLabel,
  onCancel,
  onConfirm,
}) => (
  <div
    className="fixed inset-0 z-50 overflow-y-auto"
    onKeyDown={(e) => {
      if (e.key === "Escape") onCancel();
    }}
  >
    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <div className="fixed inset-0 transition-opacity" onClick={onCancel}>
        <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="family-confirm-title"
        className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
      >
        <div className="bg-red-600 px-6 py-4">
          <h3
            id="family-confirm-title"
            className="text-xl font-semibold text-white"
          >
            {title}
          </h3>
        </div>
        <div className="bg-white dark:bg-gray-800 px-6 py-6">
          <p className="text-sm text-gray-700 dark:text-gray-300">{body}</p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            autoFocus
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  </div>
);

interface FamilyAccountCardProps {
  settings: Partial<AppSettings> | null;
  loadState: FamilyLoadState;
  loadError: string;
  onRetry: () => void;
}

const FamilyAccountCard: React.FC<FamilyAccountCardProps> = ({
  settings,
  loadState,
  loadError,
  onRetry,
}) => {
  const [accountEnabled, setAccountEnabled] = useState<boolean>(
    FAMILY_SETTING_DEFAULTS.familyAccountEnabled,
  );
  const [memberLoginEnabled, setMemberLoginEnabled] = useState<boolean>(
    FAMILY_SETTING_DEFAULTS.familyMemberLoginEnabled,
  );
  const [accountToggleInFlight, setAccountToggleInFlight] = useState(false);
  const [memberLoginInFlight, setMemberLoginInFlight] = useState(false);
  const [integerValues, setIntegerValues] = useState(() =>
    integerValuesFrom(null),
  );
  const [integerSaving, setIntegerSaving] = useState<
    Record<FamilyIntegerSettingKey, boolean>
  >({
    familyMaxMembers: false,
    familyInviteTtlDays: false,
    familyInviteDailyLimit: false,
  });
  // null = the GET answer had no allowlist: the editor stays locked.
  const [loadedAllowlist, setLoadedAllowlist] = useState<string[] | null>(
    null,
  );
  const [allowlistText, setAllowlistText] = useState("");
  const [allowlistEdited, setAllowlistEdited] = useState(false);
  const [allowlistSaving, setAllowlistSaving] = useState(false);
  const [confirm, setConfirm] = useState<FamilyConfirm>(null);
  // The settings object whose values the controls currently hold. The card
  // only counts as ready once they are applied, so no control is ever
  // clickable while it still shows a default instead of the stored value.
  const [appliedSettings, setAppliedSettings] =
    useState<Partial<AppSettings> | null>(null);

  useEffect(() => {
    if (loadState !== "ready") return;
    setAppliedSettings(settings);
    setAccountEnabled(
      settings?.familyAccountEnabled ??
        FAMILY_SETTING_DEFAULTS.familyAccountEnabled,
    );
    setMemberLoginEnabled(
      settings?.familyMemberLoginEnabled ??
        FAMILY_SETTING_DEFAULTS.familyMemberLoginEnabled,
    );
    setIntegerValues(integerValuesFrom(settings));
    const allowlist = readFamilyAllowlist(settings);
    setLoadedAllowlist(allowlist);
    setAllowlistText(allowlist ? formatFamilyAllowlist(allowlist) : "");
    setAllowlistEdited(false);
  }, [settings, loadState]);

  const ready = loadState === "ready" && appliedSettings === settings;
  const allowlistAvailable = ready && loadedAllowlist !== null;
  const parsedAllowlist = parseFamilyAllowlist(allowlistText);
  const nonCustomerIds = nonCustomerIdEntries(parsedAllowlist);
  const allowlistBlockingError = familyAllowlistError(parsedAllowlist);

  const handleAccountToggle = async () => {
    if (!ready || accountToggleInFlight) return;
    const newValue = !accountEnabled;
    setAccountToggleInFlight(true);
    try {
      await apiService.updateAppSettings({ familyAccountEnabled: newValue });
      setAccountEnabled(newValue);
      showToast(
        `Family Account ${newValue ? "enabled" : "disabled"}`,
        "success",
      );
    } catch (error) {
      showToast(familyAdminErrorMessage(error), "error");
    } finally {
      setAccountToggleInFlight(false);
    }
  };

  const saveMemberLogin = async (newValue: boolean) => {
    setMemberLoginInFlight(true);
    try {
      await apiService.updateAppSettings({
        familyMemberLoginEnabled: newValue,
      });
      setMemberLoginEnabled(newValue);
      showToast(
        `Family member logins ${newValue ? "turned on" : "turned off"}`,
        "success",
      );
    } catch (error) {
      showToast(familyAdminErrorMessage(error), "error");
    } finally {
      setMemberLoginInFlight(false);
    }
  };

  const handleMemberLoginToggle = () => {
    if (!ready || memberLoginInFlight) return;
    if (memberLoginEnabled) {
      setConfirm("memberLoginOff");
      return;
    }
    saveMemberLogin(true);
  };

  const handleIntegerSave = async (key: FamilyIntegerSettingKey) => {
    if (!ready || integerSaving[key]) return;
    const value = parseFamilyIntegerSetting(key, integerValues[key]);
    if (value === null) {
      showToast(familyIntegerSettingWarning(key), "warning");
      return;
    }
    setIntegerSaving((prev) => ({ ...prev, [key]: true }));
    try {
      await apiService.updateAppSettings({ [key]: value });
      setIntegerValues((prev) => ({ ...prev, [key]: String(value) }));
      showToast(
        `${FAMILY_INTEGER_SETTING_RULES[key].label} saved: ${value}`,
        "success",
      );
    } catch (error) {
      showToast(familyAdminErrorMessage(error), "error");
    } finally {
      setIntegerSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  const saveAllowlist = async (ids: string[]) => {
    setAllowlistSaving(true);
    try {
      await apiService.updateAppSettings({ familyAllowlistCustomerIds: ids });
      setLoadedAllowlist(ids);
      setAllowlistText(formatFamilyAllowlist(ids));
      setAllowlistEdited(false);
      showToast(
        ids.length === 1
          ? "Allowlist saved: 1 customer id"
          : `Allowlist saved: ${ids.length} customer ids`,
        "success",
      );
    } catch (error) {
      showToast(familyAdminErrorMessage(error), "error");
    } finally {
      setAllowlistSaving(false);
    }
  };

  const handleAllowlistSave = () => {
    // Guard: never send a list the admin did not edit, and never send one
    // when the GET answer had no allowlist (it would replace the stored one).
    if (!allowlistAvailable || !allowlistEdited || allowlistSaving) return;
    if (allowlistBlockingError) {
      showToast(allowlistBlockingError, "warning");
      return;
    }
    if (parsedAllowlist.length === 0 && (loadedAllowlist?.length ?? 0) > 0) {
      setConfirm("clearAllowlist");
      return;
    }
    saveAllowlist(parsedAllowlist);
  };

  const handleConfirm = () => {
    const pending = confirm;
    setConfirm(null);
    if (pending === "memberLoginOff") {
      saveMemberLogin(false);
    } else if (pending === "clearAllowlist") {
      saveAllowlist([]);
    }
  };

  return (
    <div
      className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700"
      data-testid="family-account-card"
    >
      <div className="flex items-center space-x-3 mb-3">
        <UserGroupIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Family Account
        </h2>
      </div>

      {loadState === "error" && (
        <div
          role="alert"
          className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2"
        >
          <p className="text-sm text-red-700 dark:text-red-300">
            Family Account settings could not be loaded: {loadError}
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 rounded-md hover:bg-red-100 dark:hover:bg-red-900/40"
          >
            Retry
          </button>
        </div>
      )}

      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Family Account
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Owners can invite family members and set a goal, and invitees
            can accept. While off, only families of allowlisted pilot owners
            can; existing families keep collecting EP and can still remove,
            leave or cancel invitations.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={accountEnabled}
          aria-label="Family Account"
          disabled={!ready || accountToggleInFlight}
          onClick={handleAccountToggle}
          className={switchClass(accountEnabled)}
        >
          <span className={switchKnobClass(accountEnabled)} />
        </button>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Family member logins
          </p>
          <p className="text-xs font-medium text-red-600 dark:text-red-400">
            Turning this off signs out every family member immediately.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={memberLoginEnabled}
          aria-label="Family member logins"
          disabled={!ready || memberLoginInFlight}
          onClick={handleMemberLoginToggle}
          className={switchClass(memberLoginEnabled)}
        >
          <span className={switchKnobClass(memberLoginEnabled)} />
        </button>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-4">
        {INTEGER_KEYS.map((key) => {
          const rule = FAMILY_INTEGER_SETTING_RULES[key];
          const inputId = `family-setting-${key}`;
          return (
            <div key={key} className="flex flex-col">
              <label
                htmlFor={inputId}
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {rule.label}
              </label>
              {/* flex-1 keeps the inputs on one line when a hint wraps */}
              <p className="flex-1 text-xs text-gray-500 dark:text-gray-400 mb-2">
                {rule.hint} Allowed: {rule.min}–{rule.max}.
              </p>
              <div className="flex items-center space-x-2">
                <input
                  id={inputId}
                  type="number"
                  inputMode="numeric"
                  min={rule.min}
                  max={rule.max}
                  step={1}
                  value={integerValues[key]}
                  disabled={!ready || integerSaving[key]}
                  onChange={(e) =>
                    setIntegerValues((prev) => ({
                      ...prev,
                      [key]: e.target.value,
                    }))
                  }
                  className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <button
                  type="button"
                  onClick={() => handleIntegerSave(key)}
                  disabled={!ready || integerSaving[key]}
                  aria-label={`Save ${rule.label}`}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {integerSaving[key] ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <label
          htmlFor="family-allowlist"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Pilot allowlist
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Customer ids (CC…) of owners who may use Family Account while it is
          off. One per line or separated by commas; spaces and duplicates are
          removed on save.
        </p>
        {ready && loadedAllowlist === null && (
          <div
            role="alert"
            className="mb-2 flex items-start space-x-2 rounded-md border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 px-3 py-2"
          >
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-yellow-600 dark:text-yellow-400" />
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              The allowlist was not included in the settings answer, so
              editing is disabled to keep the stored pilot list from being
              overwritten. Check that affiliate-manager-be and epoint-be share
              the same JWT secret, then reload this page.
            </p>
          </div>
        )}
        <textarea
          id="family-allowlist"
          rows={4}
          value={allowlistText}
          disabled={!allowlistAvailable || allowlistSaving}
          onChange={(e) => {
            setAllowlistText(e.target.value);
            setAllowlistEdited(true);
          }}
          placeholder={allowlistAvailable ? "CC123456\nCC654321" : ""}
          className="w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2 font-mono text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        />
        {allowlistAvailable && nonCustomerIds.length > 0 && (
          <p className="mt-1 text-xs text-yellow-700 dark:text-yellow-400">
            Customer ids start with CC. Check: {nonCustomerIds.join(", ")}
          </p>
        )}
        {allowlistAvailable && allowlistBlockingError && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">
            {allowlistBlockingError}
          </p>
        )}
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {allowlistAvailable
              ? parsedAllowlist.length === 1
                ? "1 customer id"
                : `${parsedAllowlist.length} customer ids`
              : ""}
          </span>
          <button
            type="button"
            onClick={handleAllowlistSave}
            disabled={!allowlistAvailable || !allowlistEdited || allowlistSaving}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            {allowlistSaving ? "Saving..." : "Save Allowlist"}
          </button>
        </div>
      </div>

      {confirm === "memberLoginOff" && (
        <FamilyConfirmDialog
          title="Turn off family member logins?"
          body="Turning this off signs out every family member immediately. Their accounts and data are kept, and they can sign in again once you turn logins back on."
          confirmLabel="Turn off"
          onCancel={() => setConfirm(null)}
          onConfirm={handleConfirm}
        />
      )}
      {confirm === "clearAllowlist" && (
        <FamilyConfirmDialog
          title="Clear the pilot allowlist?"
          body="Saving an empty list removes every pilot owner. While Family Account is off, they can no longer invite, accept or edit goals."
          confirmLabel="Clear allowlist"
          onCancel={() => setConfirm(null)}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
};

const Settings: React.FC = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [dealerTutorialEnabled, setDealerTutorialEnabled] = useState(true);
  const [tutorialSettingLoading, setTutorialSettingLoading] = useState(true);
  const [tutorialToggleInFlight, setTutorialToggleInFlight] = useState(false);

  // Refer-a-Friend (rate is edited as a PERCENTAGE, stored as a fraction)
  const [referralEnabled, setReferralEnabled] = useState(true);
  const [referralRatePercent, setReferralRatePercent] = useState("1");
  const [referralToggleInFlight, setReferralToggleInFlight] = useState(false);
  const [referralRateSaving, setReferralRateSaving] = useState(false);

  // Family Account card: the raw GET answer (the allowlist guard needs to
  // know whether the key was present at all).
  const [familySettings, setFamilySettings] =
    useState<Partial<AppSettings> | null>(null);
  const [familyLoadState, setFamilyLoadState] =
    useState<FamilyLoadState>("loading");
  const [familyLoadError, setFamilyLoadError] = useState("");

  const loadSettings = useCallback(() => {
    setTutorialSettingLoading(true);
    setFamilyLoadState("loading");
    apiService
      .getAppSettings()
      .then((settings) => {
        if (!settings || typeof settings !== "object") {
          throw new Error("Empty app settings answer");
        }
        setFamilySettings(settings);
        setFamilyLoadState("ready");
        setDealerTutorialEnabled(settings.dealerTutorialEnabled);
        // Older backends may not return the referral fields yet.
        setReferralEnabled(settings.referralEnabled ?? true);
        // toFixed(4): the save path quantizes to n/1e6, so percent values
        // have at most 4 decimals — this round-trips every persistable rate
        // (toFixed(2) silently rewrote finer rates on an unchanged save).
        setReferralRatePercent(
          String(((settings.referralRate ?? 0.01) * 100).toFixed(4).replace(/\.?0+$/, "")),
        );
      })
      .catch((error) => {
        setFamilyLoadError(familyAdminErrorMessage(error));
        setFamilyLoadState("error");
        toast.custom(
          <CustomToast message="Failed to load app settings" type="error" />,
          { duration: 4000 },
        );
      })
      .finally(() => setTutorialSettingLoading(false));
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleTutorialToggle = async () => {
    const newValue = !dealerTutorialEnabled;
    setTutorialToggleInFlight(true);
    try {
      await apiService.updateAppSettings({ dealerTutorialEnabled: newValue });
      setDealerTutorialEnabled(newValue);
      toast.custom(
        <CustomToast
          message={`Dealer tutorial ${newValue ? "enabled" : "disabled"}`}
          type="success"
        />,
        { duration: 4000 },
      );
    } catch (error: any) {
      toast.custom(
        <CustomToast
          message={
            error.response?.data?.message ||
            "Failed to update dealer tutorial setting"
          }
          type="error"
        />,
        { duration: 5000 },
      );
    } finally {
      setTutorialToggleInFlight(false);
    }
  };

  const handleReferralToggle = async () => {
    const newValue = !referralEnabled;
    setReferralToggleInFlight(true);
    try {
      await apiService.updateAppSettings({ referralEnabled: newValue });
      setReferralEnabled(newValue);
      toast.custom(
        <CustomToast
          message={`Refer-a-Friend ${newValue ? "enabled" : "disabled"}`}
          type="success"
        />,
        { duration: 4000 },
      );
    } catch (error: any) {
      toast.custom(
        <CustomToast
          message={
            error.response?.data?.message ||
            "Failed to update Refer-a-Friend setting"
          }
          type="error"
        />,
        { duration: 5000 },
      );
    } finally {
      setReferralToggleInFlight(false);
    }
  };

  const handleReferralRateSave = async () => {
    const percent = parseFloat(referralRatePercent);
    if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
      toast.custom(
        <CustomToast
          message="Referral rate must be between 0 and 100 percent"
          type="warning"
        />,
        { duration: 4000 },
      );
      return;
    }
    // Stored as a fraction (1% -> 0.01); rounded to avoid float dust.
    const fraction = Math.round(percent * 10000) / 1000000;
    setReferralRateSaving(true);
    try {
      await apiService.updateAppSettings({ referralRate: fraction });
      toast.custom(
        <CustomToast
          message={`Referral rate saved: ${percent}%`}
          type="success"
        />,
        { duration: 4000 },
      );
    } catch (error: any) {
      toast.custom(
        <CustomToast
          message={
            error.response?.data?.message || "Failed to update referral rate"
          }
          type="error"
        />,
        { duration: 5000 },
      );
    } finally {
      setReferralRateSaving(false);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatLastLogin = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Unknown";
    }
  };

  const handlePasswordUpdate = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.custom(
        <CustomToast message="New passwords do not match" type="warning" />,
        { duration: 4000 },
      );
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.custom(
        <CustomToast
          message="New password must be at least 6 characters"
          type="warning"
        />,
        { duration: 4000 },
      );
      return;
    }
    if (!passwordData.currentPassword) {
      toast.custom(
        <CustomToast message="Current password is required" type="warning" />,
        { duration: 4000 },
      );
      return;
    }

    setIsLoading(true);
    try {
      const response = await authService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      if (response.success) {
        toast.custom(
          <CustomToast
            message={response.message || "Password updated successfully"}
            type="success"
          />,
          { duration: 4000 },
        );
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        toast.error(response.message || "Failed to update password");
      }
    } catch (error: any) {
      toast.custom(
        <CustomToast
          message={error.response?.data?.message || "Failed to update password"}
          type="error"
        />,
        { duration: 5000 },
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Settings
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage your application settings
        </p>
      </div>

      {/* Settings Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Profile Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3 mb-3">
            <UserIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Profile
            </h2>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Username
              </label>
              <input
                type="text"
                value={user?.username || ""}
                disabled
                className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 p-2 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                value={user?.email || ""}
                disabled
                className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 p-2 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Role
              </label>
              <input
                type="text"
                value={user?.role || ""}
                disabled
                className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 p-2 cursor-not-allowed capitalize"
              />
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3 mb-3">
            <ShieldCheckIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Security
            </h2>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Current Password
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2 pr-10"
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? (
                    <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2 pr-10"
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? (
                    <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2 pr-10"
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
            <button
              onClick={handlePasswordUpdate}
              disabled={isLoading}
              className="w-full flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Updating...
                </div>
              ) : (
                <div className="flex items-center">Update Password</div>
              )}
            </button>
          </div>
        </div>

        {/* System Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3 mb-3">
            <Cog6ToothIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              System
            </h2>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                API Base URL
              </label>
              <input
                type="text"
                value={
                  process.env.REACT_APP_API_BASE_URL ||
                  "https://affiliate-api.e-point.app"
                }
                disabled
                className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 p-2 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Environment
              </label>
              <input
                type="text"
                value={process.env.NODE_ENV || "development"}
                disabled
                className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 p-2 cursor-not-allowed capitalize"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Version
              </label>
              <input
                type="text"
                value="1.0.0"
                disabled
                className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 p-2 cursor-not-allowed"
              />
            </div>
          </div>
        </div>

        {/* Dealer Onboarding */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3 mb-3">
            <Cog6ToothIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Dealer Onboarding
            </h2>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Dealer Tutorial
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Show the onboarding tutorial to dealers in the mobile app
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={dealerTutorialEnabled}
              disabled={tutorialSettingLoading || tutorialToggleInFlight}
              onClick={handleTutorialToggle}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                dealerTutorialEnabled
                  ? "bg-primary-600"
                  : "bg-gray-300 dark:bg-gray-600"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  dealerTutorialEnabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Refer-a-Friend Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3 mb-3">
            <GiftIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Refer a Friend
            </h2>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Referral Program
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Customers can invite friends and earn a bonus on every
                purchase their friends make
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={referralEnabled}
              disabled={tutorialSettingLoading || referralToggleInFlight}
              onClick={handleReferralToggle}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                referralEnabled
                  ? "bg-primary-600"
                  : "bg-gray-300 dark:bg-gray-600"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  referralEnabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Referrer bonus rate (%)
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Fixed at 1% for launch — adjustable when required. Applied by
              the Transaction Engine to every referred friend's purchase.
            </p>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={referralRatePercent}
                disabled={tutorialSettingLoading || referralRateSaving}
                onChange={(e) => setReferralRatePercent(e.target.value)}
                className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <span className="text-sm text-gray-500 dark:text-gray-400">%</span>
              <button
                type="button"
                onClick={handleReferralRateSave}
                disabled={tutorialSettingLoading || referralRateSaving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                {referralRateSaving ? "Saving..." : "Save Rate"}
              </button>
            </div>
          </div>
        </div>

        {/* Family Account Settings */}
        <FamilyAccountCard
          settings={familySettings}
          loadState={familyLoadState}
          loadError={familyLoadError}
          onRetry={loadSettings}
        />
      </div>

      {/* Session Info */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Session Information
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Manage your current session and account settings
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Last Login
            </p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {user?.lastLogin ? formatLastLogin(user.lastLogin) : "Unknown"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
